import { DOMAIN_ERROR_CODES, type ErrorDetails } from "@esi/shared";
import { en } from "./i18n/en.ts";
import { currentLocale, t } from "./i18n/i18n.ts";

/** Compatibility with one release of generic server codes; never display matched wording. */
const KNOWN = [
  [/uploaded file does not match the declared size or type/i, "errors.legacy.uploadMismatch"],
  [/readers must be named for this classification/i, "errors.legacy.readersRequired"],
  [/readers must be able to read every source/i, "files.access.refused.derived"],
  [/classified below a source/i, "files.access.refused.belowSource"],
  [/declassified content/i, "files.access.refused.declassified"],
  [/\bfrozen\b/i, "errors.codes.project_frozen"],
  [/transfer ownership to another member first/i, "settings.members.refused.ownerSelf"],
  [
    /only a transfer of ownership changes the project owner/i,
    "settings.members.refused.ownerProtected",
  ],
  [/only a transfer of ownership makes/i, "settings.members.refused.ceiling"],
  [/owner role cannot be granted/i, "settings.members.refused.ceiling"],
  [/personal project's owner is its owning user/i, "settings.members.refused.personalTransfer"],
  [/ownership can only go to a direct member/i, "settings.members.refused.notDirect"],
  [/ownership cannot go to break-glass/i, "settings.members.refused.breakGlassOwner"],
  [/shareOutsideEntity/i, "settings.members.refused.outsideEntity"],
  [/break-glass access is always reader/i, "settings.members.refused.breakGlass"],
  [/already a member/i, "settings.members.refused.already"],
  [/user is disabled/i, "settings.members.refused.disabled"],
  [/can only be narrowed for this project/i, "settings.behaviour.refused.narrowOnly"],
  [/cannot be changed for this project/i, "settings.behaviour.refused.locked"],
  [/governance never gives you a role/i, "governance.selfRefused"],
  [/end your break-glass access to this project/i, "governance.breakGlassActive"],
] as const;

/** Catalogue explanations for known refusals; callers apply the locale-specific fallback. */
export function errorCopy(
  code: string | undefined,
  legacyMessage?: string,
  details?: ErrorDetails,
): string | undefined {
  // Generic codes on older servers need their known wording to retain a useful explanation.
  if (!code || ["conflict", "forbidden", "invalid", "unprocessable", "refused"].includes(code)) {
    const known = KNOWN.find(([pattern]) => pattern.test(legacyMessage ?? ""));
    if (known) {
      return t(known[1]);
    }
    const domain = /Invalid domain "([^"]*)"/.exec(legacyMessage ?? "");
    if (domain) {
      return t("settings.behaviour.refused.invalidDomain", { domain: domain[1] ?? "" });
    }
  }
  if (legacyMessage && (!code || DOMAIN_ERROR_CODES.some((generic) => generic === code))) {
    return undefined;
  }
  // Preserve detailed validation guidance for English until its constraints have structured translations.
  if (code === "invalid_setting" && legacyMessage && currentLocale() === "en") {
    return undefined;
  }
  // A refused wiki item names its OKF check (wiki-okf-structure D12): say which, and how to fix it.
  const check = details?.check;
  if (
    code === "wiki_item_invalid" &&
    typeof check === "string" &&
    Object.hasOwn(en.wiki.okfChecks.errors, check)
  ) {
    return t(`wiki.okfChecks.errors.${check as keyof typeof en.wiki.okfChecks.errors}`);
  }
  if (code && Object.hasOwn(en.errors.codes, code)) {
    const key = code as keyof typeof en.errors.codes;
    const parameters = [...en.errors.codes[key].matchAll(/{{(\w+)}}/g)].map((match) => match[1]);
    const values: ErrorDetails = {};
    for (const parameter of parameters) {
      const value = parameter ? details?.[parameter] : undefined;
      if (!parameter || (typeof value !== "string" && typeof value !== "number")) {
        return undefined;
      }
      values[parameter] = value;
    }
    // Response metadata supplies interpolation values, never i18next control options.
    return t(`errors.codes.${key}`, values);
  }
  return undefined;
}

/** Resolve the previous release's generic frozen-project refusal before any display handling. */
export function resolveErrorCode(code: string | undefined, message?: string): string | undefined {
  return (!code || code === "conflict") && /\bfrozen\b/i.test(message ?? "")
    ? "project_frozen"
    : code;
}
